import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"
import { showToast } from "../utils/toast.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  failureMessage: "",
  closeLabel: ""
}

export default class extends Controller {
  static values = {
    translations: { type: Object, default: {} },
    frameId: String,
    tableKey: String,
    canonicalQuery: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.#publishCanonicalQuery()
    this.boundNavigationFeedback = this.#handleNavigationFeedback.bind(this)
    document.addEventListener("lucanto:table-navigation-feedback", this.boundNavigationFeedback)
    this.navigator = new TableFrameNavigator({ frameId: this.frameIdValue, tableKey: this.tableKeyValue || null })
  }

  #publishCanonicalQuery() {
    if (!this.hasCanonicalQueryValue) return

    const canonical = new TableQueryState(this.canonicalQueryValue).toSearchParams().toString()
    const current = new TableQueryState(window.location.search).toSearchParams().toString()
    if (canonical === current) return

    const url = new URL(window.location.href)
    url.search = canonical
    window.history.replaceState(window.history.state, "", url)
  }

  disconnect() {
    document.removeEventListener("lucanto:table-navigation-feedback", this.boundNavigationFeedback)
    this.navigator?.disconnect()
  }

  navigate(event) {
    const link = event.target.closest("a[href][data-table-navigation-reason]")
    if (!link || !this.element.contains(link) || !this.#canHandle(event, link)) return

    const url = new URL(link.href, window.location.href)
    if (url.origin !== window.location.origin) return

    event.preventDefault()
    const reason = link.dataset.tableNavigationReason
    this.navigator.navigate(new TableQueryState(url.search), {
      history: "push",
      reason,
      baseUrl: `${url.origin}${url.pathname}`,
      preserveScroll: reason === "saved-view" ? { x: false, y: false } : { x: true, y: false }
    })
  }

  #canHandle(event, link) {
    return (
      event.button === 0 &&
      !event.defaultPrevented &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey &&
      link.target !== "_blank" &&
      !link.hasAttribute("download")
    )
  }

  #handleNavigationFeedback(event) {
    const { frameId, tableKey, type, code } = event.detail || {}
    if (frameId !== this.frameIdValue || (tableKey && tableKey !== this.tableKeyValue)) return
    if (type !== "error" || code !== "navigation_failed" || !this.#texts.failureMessage) return

    showToast({
      type: "error",
      message: this.#texts.failureMessage,
      closeLabel: this.#texts.closeLabel || "Close"
    })
  }
}
